import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import SignatureModal from './components/SignatureModal.mjs';

/**
 * requestSignature() from anywhere under the provider - the signing modal as a
 * promise, the way useDialog().confirm is:
 *
 *   const signature = await requestSignature({ reportId, title, reason, applyLabel });
 *   if (!signature) return;            // backed out
 *   await approve({ ..., signature }); // the payload the server checks
 *
 * Resolves null when the signer cancels.
 */
const SignatureContext = createContext(null);

export const useSignature = () => {
  const ctx = useContext(SignatureContext);
  if (ctx) return ctx;
  // Outside a provider (a component mounted bare in a test) nothing can sign.
  return { requestSignature: async () => null };
};

export const SignatureProvider = ({ children }) => {
  const [request, setRequest] = useState(null);
  const resolverRef = useRef(null);

  const settle = useCallback((value) => {
    const resolve = resolverRef.current;
    resolverRef.current = null;
    setRequest(null);
    if (resolve) resolve(value);
  }, []);

  const requestSignature = useCallback((options) => new Promise((resolve) => {
    // A second request while one is open would strand the first promise.
    if (resolverRef.current) resolverRef.current(null);
    resolverRef.current = resolve;
    setRequest(options);
  }), []);

  const api = useMemo(() => ({ requestSignature }), [requestSignature]);

  return (
    <SignatureContext.Provider value={api}>
      {children}
      {request && (
        <SignatureModal
          {...request}
          onApply={(signature) => settle(signature)}
          onCancel={() => settle(null)}
        />
      )}
    </SignatureContext.Provider>
  );
};

SignatureProvider.propTypes = {
  children: PropTypes.node,
};

export default SignatureProvider;
